import React, { useEffect, useState } from 'react'
import { Button } from '~/components/common/Button'
import { Input } from '~/components/common/CurieInput'
import { ErrorLogTerminal } from '~/components/common/ErrorLogTerminal'
import { Typography } from '~/components/common/Typography'
import { cm } from '~/utils/utils'
import { ConnectAzureGuide } from './ConnectAzureGuide'

/** Lifecycle of the agent-driven Azure deploy, as surfaced in the modal. */
export type AzureDeployPhase =
  | 'needs_connection'
  | 'deploying'
  | 'awaiting_input'
  | 'done'
  | 'failed'

export type DeployInputKind = 'text' | 'confirm'

/**
 * A single thing the running deploy needs from the user. Emitted by the agent
 * when a phase can't proceed without input (the risks/assumptions pause pattern).
 */
export interface DeployInputRequest {
  /** Stable id so the input footer resets its local state between requests. */
  id?: string
  kind: DeployInputKind
  /** Prompt shown above the controls, e.g. "Storage account name". */
  label: string
  /** Text kind only. */
  placeholder?: string
  defaultValue?: string
  /** Mask the value (e.g. an API key). Text kind only. */
  isSecret?: boolean
  /** Confirm kind only — defaults to "Yes" / "No". */
  confirmLabel?: string
  denyLabel?: string
}

export interface AzureDeployStep {
  label: string
  status: 'done' | 'active' | 'pending'
}

const DEFAULT_STEPS: AzureDeployStep[] = [
  { label: 'Docs', status: 'done' },
  { label: 'API spec', status: 'done' },
  { label: 'Code', status: 'done' },
  { label: 'Deploy', status: 'active' }
]

export interface AzureDeployModalProps {
  isOpen: boolean
  phase: AzureDeployPhase
  /** Accumulated terminal log (stdout/stderr) for the deploy run. */
  log: string
  /** Migration step strip; defaults to Docs / API spec / Code / Deploy(active). */
  steps?: AzureDeployStep[]
  /** Rendered below the terminal when phase === 'awaiting_input'. */
  inputRequest?: DeployInputRequest
  /** User submitted a text answer (kind === 'text'). */
  onSubmitText?: (value: string) => void
  /** User answered a confirm prompt (kind === 'confirm'). */
  onConfirm?: (approved: boolean) => void
  /** Disables the input controls while a response is in flight. */
  isSubmitting?: boolean
  /** Trigger/callback URL to surface on success (phase === 'done'). */
  triggerUrl?: string
  /** Optional retry handler shown on failure (phase === 'failed'). */
  onRetry?: () => void
  /** Opens the Connect Azure form (phase === 'needs_connection'). */
  onConnect?: () => void
  className?: string
}

const PHASE_BADGE: Record<AzureDeployPhase, { label: string; className: string }> = {
  needs_connection: {
    label: 'Connect Azure',
    className: 'bg-tokens-status-warning-3 text-tokens-status-warning-1'
  },
  deploying: { label: 'Deploying', className: 'bg-tokens-status-link-3 text-tokens-status-link-1' },
  awaiting_input: {
    label: 'Awaiting input',
    className: 'bg-tokens-status-warning-3 text-tokens-status-warning-1'
  },
  done: { label: 'Deployed', className: 'bg-tokens-status-success-3 text-tokens-status-success-1' },
  failed: { label: 'Failed', className: 'bg-tokens-status-error-10 text-tokens-status-error-text' }
}

const CloudIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path
      d='M7 18a4 4 0 0 1 0-8 5 5 0 0 1 9.6-1.5A4.5 4.5 0 0 1 17 18H7Z'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinejoin='round'
    />
    <path
      d='M12 15V9m0 0-2.2 2.2M12 9l2.2 2.2'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const StepStrip = ({ steps }: { steps: AzureDeployStep[] }) => (
  <div
    className='flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-tokens-neutral-border-2 px-6 py-2.5'
    data-testid='azure-deploy-steps'
  >
    {steps.map((step, i) => (
      <React.Fragment key={step.label}>
        {i > 0 && (
          <Typography fontFamily='geist' fontSize='labelSm' className='text-neutral-text-tertiary'>
            ·
          </Typography>
        )}
        <Typography
          fontFamily='geist'
          fontSize='labelSm'
          fontWeight={step.status === 'active' ? 'medium' : 'normal'}
          className={cm(
            step.status === 'active' ? 'text-neutral-text-primary' : 'text-neutral-text-secondary'
          )}
        >
          {step.status === 'done' && (
            <span className='text-tokens-status-success-1' aria-hidden='true'>
              ✓{' '}
            </span>
          )}
          {step.status === 'active' && <span aria-hidden='true'>▶ </span>}
          {step.label}
        </Typography>
      </React.Fragment>
    ))}
  </div>
)

const RunningLine = () => (
  <div className='flex items-center gap-2 px-1' data-testid='azure-deploy-running'>
    <span
      className='size-2 animate-pulse rounded-full bg-tokens-status-link-1'
      aria-hidden='true'
    />
    <Typography fontFamily='geist' fontSize='labelSm' className='text-neutral-text-secondary'>
      running…
    </Typography>
  </div>
)

/** The conditional input footer. Keyed by request id/label so it resets per prompt. */
const DeployInputFooter = ({
  request,
  isSubmitting,
  onSubmitText,
  onConfirm
}: {
  request: DeployInputRequest
  isSubmitting?: boolean
  onSubmitText?: (value: string) => void
  onConfirm?: (approved: boolean) => void
}) => {
  const [value, setValue] = useState(request.defaultValue ?? '')

  // Defensive: if the same component instance is reused for a new request
  // without a key change, still resync the seed value.
  useEffect(() => {
    setValue(request.defaultValue ?? '')
  }, [request.id, request.label, request.defaultValue])

  return (
    <div
      className='rounded-[12px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1 p-3'
      data-testid='azure-deploy-input'
    >
      <Typography
        fontFamily='geist'
        fontSize='labelSm'
        className='mb-2 block text-neutral-text-secondary'
      >
        {request.label}
      </Typography>

      {request.kind === 'confirm' ? (
        <div className='flex items-center gap-2'>
          <Button
            variant='primary'
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            onClick={() => onConfirm?.(true)}
          >
            {request.confirmLabel ?? 'Yes'}
          </Button>
          <Button variant='outlined' isDisabled={isSubmitting} onClick={() => onConfirm?.(false)}>
            {request.denyLabel ?? 'No'}
          </Button>
        </div>
      ) : (
        <div className='flex items-center gap-2'>
          <Input
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={request.placeholder}
            type={request.isSecret ? 'password' : 'text'}
            disabled={isSubmitting}
            autoFocus
            height='md'
            className='flex-1'
            onKeyDown={e => {
              if (e.key === 'Enter' && value.trim()) onSubmitText?.(value)
            }}
          />
          <Button
            variant='primary'
            isDisabled={isSubmitting || !value.trim()}
            isLoading={isSubmitting}
            onClick={() => onSubmitText?.(value)}
          >
            Submit
          </Button>
        </div>
      )}
    </div>
  )
}

const TerminationFooter = ({
  phase,
  triggerUrl,
  onRetry
}: {
  phase: 'done' | 'failed'
  triggerUrl?: string
  onRetry?: () => void
}) => {
  // Dismissal is handled by the header's close (✕); this footer only surfaces
  // the success trigger URL and a failure retry affordance.
  const showUrl = phase === 'done' && !!triggerUrl
  const showRetry = phase === 'failed' && !!onRetry
  if (!showUrl && !showRetry) return null

  return (
    <div className='flex flex-col gap-3' data-testid='azure-deploy-termination'>
      {showUrl && (
        <div className='rounded-[12px] border border-tokens-status-success-2 bg-tokens-neutral-bg-grey-1 p-3'>
          <Typography
            fontFamily='geist'
            fontSize='labelSm'
            className='mb-1 block text-neutral-text-secondary'
          >
            Trigger URL
          </Typography>
          <a
            href={triggerUrl}
            target='_blank'
            rel='noreferrer'
            className='break-all font-mono text-[12px] text-tokens-status-link-1 underline'
          >
            {triggerUrl}
          </a>
        </div>
      )}
      {showRetry && (
        <div className='flex items-center justify-end gap-2'>
          <Button variant='outlined' onClick={onRetry}>
            Retry
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Modal that surfaces the agent-driven Azure deploy as a terminal: a monospace
 * log on top, and a conditional footer below that shows input controls when a
 * phase needs information, or a close/retry affordance once the run ends.
 *
 * Purely presentational — phase, log, and input requests are driven by the
 * caller (the migration task state). See DEPLOY_AUTOMATION_DESIGN.md.
 */
export const AzureDeployModal: React.FC<AzureDeployModalProps> = ({
  isOpen,
  phase,
  log,
  steps = DEFAULT_STEPS,
  inputRequest,
  onSubmitText,
  onConfirm,
  isSubmitting,
  triggerUrl,
  onRetry,
  onConnect,
  className
}) => {
  const badge = PHASE_BADGE[phase]

  if (!isOpen) return null

  // Renders inline in the task timeline as a contained card (no modal/overlay).
  return (
    <div
      className={cm(
        'flex w-full flex-col overflow-hidden rounded-[12px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white shadow-popover',
        className
      )}
      data-testid='azure-deploy-modal'
    >
      <div className='flex items-center justify-between border-b border-tokens-neutral-border-2 px-6 py-4'>
        <div className='flex items-center gap-2'>
          <CloudIcon className='text-tokens-status-link-1' />
          <Typography fontFamily='geist' fontSize='titleMd' fontWeight='bold'>
            Deploy to Azure
          </Typography>
        </div>
        <span
          className={cm('rounded-[8px] px-3 py-1 text-[12px] font-medium', badge.className)}
          data-testid='azure-deploy-status'
        >
          {badge.label}
        </span>
      </div>

      <StepStrip steps={steps} />

      <div className='flex flex-col gap-3.5 px-6 py-4'>
        {phase === 'needs_connection' ? (
          <ConnectAzureGuide onConnect={onConnect} />
        ) : (
          <>
            <ErrorLogTerminal
              value={log}
              contentClassName='max-h-[260px]'
              data-testid='azure-deploy-log'
            />

            {phase === 'deploying' && <RunningLine />}

            {phase === 'awaiting_input' && inputRequest && (
              <DeployInputFooter
                key={inputRequest.id ?? inputRequest.label}
                request={inputRequest}
                isSubmitting={isSubmitting}
                onSubmitText={onSubmitText}
                onConfirm={onConfirm}
              />
            )}

            {(phase === 'done' || phase === 'failed') && (
              <TerminationFooter phase={phase} triggerUrl={triggerUrl} onRetry={onRetry} />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default AzureDeployModal
